import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  Spin,
  Alert,
  Select,
  Form,
  Input,
  Button,
  Row,
  Col,
  Checkbox,
  notification,
  Typography,
  Tooltip,
  Drawer,
  Descriptions,
  Divider,
  Pagination,
  Carousel,
} from "antd";
import dayjs from "dayjs";

import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  DollarOutlined,
  CreditCardOutlined,
  BankOutlined,
  UserOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import useFetchData from "shared/hooks/useFetchData";
import axios from "axios";
import CustomModal from "shared/components/CustomModal";
import "./MyComponent.css";
import { ColumnType } from "antd/es/table";
import { useMediaQuery } from "react-responsive";
import "./BookList.css";
import { API_BASE_URL } from "shared/api/config";

interface GiftPhoto {
  size: string;
  count: number;
  _id: string;
}

interface Paper {
  size: string;
  count: number;
  _id: string;
}

interface Frame {
  size: string;
  count: number;
  _id: string;
}

interface FrameAndPaper {
  size: string;
  count: number;
  _id: string;
}
interface Canvas {
  size: string;
  code: string;
  price: number;
  realPrice: number;
  _id: string;
}
interface PaymentBreakdown {
  cash: number;
  card: number;
  account: number;
}
export interface BookUser {
  _id?: string;
  name?: string;
  fullName?: string;
  username?: string;
  userrealname?: string;
  role?: string;
}
interface DataType {
  _id: string;
  key: React.Key;
  year: string;
  day: string;
  bookedTime: string;
  packageName: string;
  giftCardId?: string;
  giftCardCode?: string;
  giftCardPackage?: string;
  giftCardContact?: string;
  prePay: number;
  postPay: number;
  addPayment: number;
  minusPayment: number;
  giftPhoto: boolean;
  pictures: GiftPhoto[];
  paper: Paper[];
  frame: Frame[];
  frameAndPaper: FrameAndPaper[];
  canvas: Canvas[];
  paymenType: string;
  paymentBreakdown?: PaymentBreakdown;
  description: string;
  createdBy: string | BookUser | null;
  pickedUpCanvas: boolean;
}

type EditableBookValues = Partial<DataType> & {
  bookingDate?: any;
  bookingTime?: any;
};
interface ApiResponse {
  success: boolean;
  message: string;
  data: DataType[];
  total: number;
  page: number;
  totalPages: number;
}
interface BookingSiteBooking {
  id: string;
  eventId: string;
  eventTitle: string;
  status?: "active" | "cancelled";
  date: string;
  time: string;
  name: string;
  email: string;
  phone: string;
  note: string;
  createdAt: string;
  updatedAt?: string;
  cancelledAt?: string;
  cancelledReason?: string;
  rescheduledFrom?: {
    eventTitle: string;
    date: string;
    time: string;
  };
}

interface BookingSiteApiResponse {
  success: boolean;
  bookings: BookingSiteBooking[];
  total: number;
}

interface PackageOption {
  value: string;
  label: string;
  price: number;
}
interface PackageGroup {
  label: string;
  options: PackageOption[];
}
interface UserFilterOption {
  label: string;
  value: string;
  searchText: string;
}

const normalizePaymentBreakdown = (
  paymentBreakdown?: Partial<PaymentBreakdown>,
  legacyType?: string,
  legacyAmount?: number
): PaymentBreakdown => {
  const normalized = {
    cash: Number(paymentBreakdown?.cash || 0),
    card: Number(paymentBreakdown?.card || 0),
    account: Number(paymentBreakdown?.account || 0),
  };

  if (
    normalized.cash + normalized.card + normalized.account === 0 &&
    legacyType &&
    legacyType !== "Mixed" &&
    Number(legacyAmount || 0) > 0
  ) {
    const key = legacyType.toLowerCase() as keyof PaymentBreakdown;
    if (key in normalized) {
      normalized[key] = Number(legacyAmount || 0);
    }
  }

  return normalized;
};

const getPaymentBreakdownTotal = (paymentBreakdown?: Partial<PaymentBreakdown>) =>
  Number(paymentBreakdown?.cash || 0) +
  Number(paymentBreakdown?.card || 0) +
  Number(paymentBreakdown?.account || 0);

const getPrimaryPaymentType = (paymentBreakdown?: Partial<PaymentBreakdown>) => {
  const entries = [
    ["Cash", Number(paymentBreakdown?.cash || 0)],
    ["Card", Number(paymentBreakdown?.card || 0)],
    ["Account", Number(paymentBreakdown?.account || 0)],
  ].filter(([, amount]) => Number(amount) > 0);

  if (entries.length > 1) return "Mixed";
  return String(entries[0]?.[0] || "");
};

const sortDaysFromToday = (days: string[], year: string) => {
  const today = dayjs();
  const selectedYear = Number(year);
  const pivot = selectedYear === today.year() ? today.startOf("day") : dayjs(`${year}-01-01`);

  return [...days].sort((first, second) => {
    const firstDate = dayjs(`${year}-${first}`);
    const secondDate = dayjs(`${year}-${second}`);
    const firstIsUpcoming =
      firstDate.isValid() && (firstDate.isSame(pivot, "day") || firstDate.isAfter(pivot));
    const secondIsUpcoming =
      secondDate.isValid() && (secondDate.isSame(pivot, "day") || secondDate.isAfter(pivot));

    if (firstIsUpcoming !== secondIsUpcoming) {
      return firstIsUpcoming ? -1 : 1;
    }

    return firstIsUpcoming
      ? firstDate.valueOf() - secondDate.valueOf()
      : secondDate.valueOf() - firstDate.valueOf();
  });
};

const ULAANBAATAR_TIME_ZONE = "Asia/Ulaanbaatar";
const MATCH_TIME_THRESHOLD_MIN = 30;
const EXACT_TIME_THRESHOLD_MIN = 5;
const PACKAGE_MATCH_THRESHOLD = 0.42;

const normalizeMatchText = (value?: string) =>
  String(value || "")
    .toLowerCase()
    .replace(/[()（）.,:;!?'"`[\]{}₮₩$№_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getMatchTokens = (value?: string) =>
  normalizeMatchText(value)
    .split(" ")
    .map((token) => token.trim())
    .filter((token) => token.length > 1);

const getTextSimilarity = (first?: string, second?: string) => {
  const normalizedFirst = normalizeMatchText(first).replace(/\s+/g, "");
  const normalizedSecond = normalizeMatchText(second).replace(/\s+/g, "");

  if (!normalizedFirst || !normalizedSecond) return 0;
  if (normalizedFirst === normalizedSecond) return 1;
  if (
    normalizedFirst.includes(normalizedSecond) ||
    normalizedSecond.includes(normalizedFirst)
  ) {
    return 0.9;
  }

  const firstTokens = new Set(getMatchTokens(first));
  const secondTokens = new Set(getMatchTokens(second));
  if (firstTokens.size === 0 || secondTokens.size === 0) return 0;

  const commonCount = Array.from(firstTokens).filter((token) =>
    secondTokens.has(token)
  ).length;

  return commonCount / Math.min(firstTokens.size, secondTokens.size);
};

const getPackageMatchScore = (
  eventName: string,
  packageCandidates: Array<string | undefined>
) =>
  Math.max(
    0,
    ...packageCandidates.map((candidate) =>
      getTextSimilarity(eventName, candidate)
    )
  );

const isPortraitPackage = (value?: string) => {
  const normalized = normalizeMatchText(value);

  return normalized.includes("portrait") || normalized.includes("хөрөг");
};

const isPackageFamilyCompatible = (
  eventName: string,
  packageCandidates: Array<string | undefined>
) => {
  const eventIsPortrait = isPortraitPackage(eventName);
  const bookingIsPortrait = packageCandidates.some(isPortraitPackage);

  if (eventIsPortrait || bookingIsPortrait) {
    return eventIsPortrait === bookingIsPortrait;
  }

  return true;
};

const getBookingDate = (record: DataType) => {
  const [month, day] = String(record.day || "").split("-");
  if (!record.year || !month || !day || !record.bookedTime) return null;

  const bookingDate = new Date(
    `${record.year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T${
      record.bookedTime
    }:00+08:00`
  );

  return Number.isNaN(bookingDate.getTime()) ? null : bookingDate;
};

const getDateKeyInUlaanbaatar = (date: Date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: ULAANBAATAR_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const getPart = (type: string) =>
    parts.find((part) => part.type === type)?.value || "";

  return `${getPart("year")}-${getPart("month")}-${getPart("day")}`;
};

const parseBookingSiteNote = (note = "") =>
  note.split(";").reduce(
    (acc, part) => {
      const [key, ...rest] = part.split("=");
      const value = rest.join("=").trim();
      const normalizedKey = key?.trim();

      if (normalizedKey === "quantity") acc.quantity = value;
      if (normalizedKey === "gift") acc.gift = value;
      if (normalizedKey === "sms") acc.sms = value;
      if (normalizedKey === "childGender") acc.childGender = value;

      return acc;
    },
    { quantity: "", gift: "", sms: "", childGender: "" }
  );

// const PACKAGE_OPTIONS: PackageOption[] = [
//   {
//     value: "60м(Reg)",
//     label: "60минутын багц ( Үнэ:60,000₮  4-6хүн)",
//     price: 60000,
//   },
//   {
//     value: "30м(Reg)",
//     label: "30минутын багц ( Үнэ:40,000₮ 1-4 хүн ).",
//     price: 40000,
//   },
//   {
//     value: "60м Жир(Reg)",
//     label: "Жирэмсний багц ( Үнэ:80,000₮) 60минут",
//     price: 80000,
//   },
//   {
//     value: "60м(child)",
//     label: "Хүүхдийн зургын багц ( Үнэ:100,000₮ ) 60минут",
//     price: 100000,
//   },
//   {
//     value: "60м(Por)",
//     label: "Portrait Багц (60минут Үнэ:100,000₮ хөрөг зураг)",
//     price: 100000,
//   },
//   {
//     value: "30м(Por)",
//     label: "Portrait Багц (30минут Үнэ:50,000₮ хөрөг зураг)",
//     price: 50000,
//   },
//   {
//     value: "60м Жир(Por)",
//     label: "Жирэмсний багц Portrait ( Үнэ: 100,000₮) 60минут",
//     price: 100000,
//   },
// ];
interface MyComponentProps {
  theme?: string; // Optional theme prop if needed
}
const MyComponent: React.FC<MyComponentProps> = ({ theme }) => {
  const currentTheme = theme || "light";
  const currentYear = String(new Date().getFullYear());
  const [selectedYear, setSelectedYear] = useState<string>(currentYear);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState<string | null>(
    null
  );
  const [selectedPackage, setSelectedPackage] = useState<string[] | null>(null);
  const [form] = Form.useForm();
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [footerPassword, setFooterPassword] = useState("");
  const [selectedUserRole, setSelectedUserRole] = useState("");
  const [descriptionSearch, setDescriptionSearch] = useState<string>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<
    "view" | "edit" | "delete" | "password" | null
  >(null);
  const [originalRecord, setOriginalRecord] = useState<DataType | null>(null);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [currentRow, setCurrentRow] = useState<DataType | null>(null);
  const [canvasOnly, setCanvasOnly] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [packages, setPackages] = useState<PackageGroup[]>([]);
  const [authUsers, setAuthUsers] = useState<BookUser[]>([]);
  const [filterSourceData, setFilterSourceData] = useState<DataType[]>([]);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/package/get`)
      .then((res) => {
        setPackages(Array.isArray(res.data) ? res.data : res.data.data || []);
      })
      .catch((err) => {
        console.error("Package fetch error:", err);
      });
  }, []);

  const getCreatedByName = (createdBy: any): string => {
    if (!createdBy) return "";

    if (typeof createdBy === "string") return createdBy;

    return (
      createdBy.fullName ||
      createdBy.name ||
      createdBy.userrealname ||
      createdBy.username ||
      createdBy._id ||
      ""
    );
  };
  const flatPackages: PackageOption[] = packages.flatMap(
    (group) => group.options
  );

  useEffect(() => {
    const fetchFilterSource = async () => {
      try {
        const params = new URLSearchParams({
          page: "1",
          limit: "5000",
          year: selectedYear,
        });
        const res = await axios.get(`${API_BASE_URL}/book/get?${params.toString()}`);
        setFilterSourceData(res.data?.data || []);
      } catch (err) {
        console.error("Booking filter source fetch error:", err);
      }
    };

    fetchFilterSource();
  }, [selectedYear]);

  const bookingQuery = useMemo(() => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(pageSize),
      year: selectedYear,
    });

    if (selectedDay) {
      params.set("day", selectedDay);
    }

    if (selectedPackage?.length) {
      params.set("packageName", selectedPackage.join(","));
    }

    if (selectedPaymentType) {
      params.set("paymentType", selectedPaymentType);
    }

    if (selectedUserRole) {
      params.set("createdBy", selectedUserRole);
    }

    if (descriptionSearch.trim()) {
      params.set("description", descriptionSearch.trim());
    }

    if (canvasOnly) {
      params.set("canvasOnly", "true");
    }

    return `/book/get?${params.toString()}`;
  }, [
    canvasOnly,
    descriptionSearch,
    page,
    pageSize,
    selectedDay,
    selectedPackage,
    selectedPaymentType,
    selectedUserRole,
    selectedYear,
  ]);

  const {
    data: bookingData,
    loading,
    error,
    refetch,
  } = useFetchData<ApiResponse>(bookingQuery);
  const { Title } = Typography;
  const bookingSiteQuery = useMemo(() => {
    const params = new URLSearchParams({
      status: "active",
      rangeStart: `${selectedYear}-01-01`,
      rangeEnd: `${selectedYear}-12-31`,
      limit: "3000",
    });

    return `/booking-site/bookings?${params.toString()}`;
  }, [selectedYear]);
  const { data: bookingSiteData } =
    useFetchData<BookingSiteApiResponse>(bookingSiteQuery);
  const [bookingSiteBookings, setBookingSiteBookings] = useState<
    BookingSiteBooking[]
  >([]);
  const [bookingSiteMatches, setBookingSiteMatches] = useState<
    BookingSiteBooking[]
  >([]);
  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem("token");
      if (!token) return;

      const payload = JSON.parse(atob(token.split(".")[1]));
      const now = Date.now() / 1000;

      if (payload.exp < now) {
        localStorage.clear();
        window.location.href = "/login";
      }
    }, 5000); // 5 sec тутам шалгана

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/auth`);

        setAuthUsers(res.data || []);
      } catch (err) {
        console.error("Users fetch error:", err);
      }
    };

    fetchUsers();
  }, []);

  const isMobile = useMediaQuery({ maxWidth: 768 });

  useEffect(() => {
    setBookingSiteBookings(
      (bookingSiteData?.bookings || []).filter(
        (booking) => booking.status !== "cancelled"
      )
    );
  }, [bookingSiteData]);

  const findBookingSiteMatches = (record: DataType) => {
    const matchedOption = flatPackages.find(
      (pkg) => pkg.value === record.packageName
    );
    const bookingDate = getBookingDate(record);
    if (!bookingDate) return [];

    const packageCandidates = [
      record.packageName,
      matchedOption?.value,
      matchedOption?.label,
    ];
    const bookingDateKey = getDateKeyInUlaanbaatar(bookingDate);

    const scoredMatches = bookingSiteBookings
      .map((booking) => {
        const bookingSiteDate = new Date(`${booking.date}T${booking.time}:00+08:00`);
        const timeDiffMin =
          Math.abs(bookingDate.getTime() - bookingSiteDate.getTime()) / 60000;
        const packageScore = getPackageMatchScore(
          booking.eventTitle,
          packageCandidates
        );

        return {
          booking,
          packageScore,
          familyCompatible: isPackageFamilyCompatible(
            booking.eventTitle,
            packageCandidates
          ),
          timeDiffMin,
          sameDay:
            !Number.isNaN(bookingSiteDate.getTime()) &&
            getDateKeyInUlaanbaatar(bookingSiteDate) === bookingDateKey,
        };
      })
      .filter(({ familyCompatible, packageScore, sameDay, timeDiffMin }) => {
        if (!familyCompatible) return false;

        const isPackageAndTimeMatch =
          sameDay &&
          timeDiffMin <= MATCH_TIME_THRESHOLD_MIN &&
          packageScore >= PACKAGE_MATCH_THRESHOLD;
        const isExactTimeFallback = sameDay && timeDiffMin <= EXACT_TIME_THRESHOLD_MIN;

        return isPackageAndTimeMatch || isExactTimeFallback;
      })
      .sort((first, second) => {
        const firstPackageMatched =
          first.packageScore >= PACKAGE_MATCH_THRESHOLD ? 1 : 0;
        const secondPackageMatched =
          second.packageScore >= PACKAGE_MATCH_THRESHOLD ? 1 : 0;

        if (firstPackageMatched !== secondPackageMatched) {
          return secondPackageMatched - firstPackageMatched;
        }

        if (first.timeDiffMin !== second.timeDiffMin) {
          return first.timeDiffMin - second.timeDiffMin;
        }

        return second.packageScore - first.packageScore;
      });

    return scoredMatches.map(({ booking }) => booking);
  };

  const showDrawer = (record: DataType) => {
    const matchedBookings = findBookingSiteMatches(record);

    setBookingSiteMatches(matchedBookings);
    setCurrentRow(record);
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const normalizeEditableBookValues = useCallback((values: EditableBookValues) => {
    const bookingDate = values.bookingDate;
    const bookingTime = values.bookingTime;
    const paymentBreakdown = normalizePaymentBreakdown(
      values.paymentBreakdown,
      values.paymenType,
      values.postPay
    );

    return {
      year:
        bookingDate && typeof bookingDate.format === "function"
          ? bookingDate.format("YYYY")
          : values.year || "",
      day:
        bookingDate && typeof bookingDate.format === "function"
          ? bookingDate.format("MM-DD")
          : values.day || "",
      bookedTime:
        bookingTime && typeof bookingTime.format === "function"
          ? bookingTime.format("HH:mm")
          : values.bookedTime || "",
      packageName: Array.isArray(values.packageName)
        ? values.packageName[0] || ""
        : values.packageName || "",
      prePay: Number(values.prePay || 0),
      postPay: Number(values.postPay || 0),
      addPayment: Number(values.addPayment || 0),
      minusPayment: Number(values.minusPayment || 0),
      giftPhoto: Boolean(values.giftPhoto),
      pictures: values.pictures || [],
      paper: values.paper || [],
      frame: values.frame || [],
      frameAndPaper: values.frameAndPaper || [],
      canvas: values.canvas || [],
      paymenType: getPrimaryPaymentType(paymentBreakdown),
      paymentBreakdown,
      description: values.description || "",
      pickedUpCanvas: Boolean(values.pickedUpCanvas),
    };
  }, []);

  const calculateEditablePostPay = (values: EditableBookValues) => {
    const pkgList = packages.flatMap((group) => group.options);
    const packagePrice = Array.isArray(values.packageName)
      ? values.packageName.reduce((sum, packageName) => {
          const pkg = pkgList.find((option) => option.value === packageName);
          return sum + (pkg?.price || 0);
        }, 0)
      : pkgList.find((option) => option.value === values.packageName)?.price || 0;

    const calcItems = (
      items: Array<{ size?: string; count?: number }> = [],
      options: Array<{ value: string; price: number }>
    ) =>
      (items || []).reduce((sum, item) => {
        const price = options.find((option) => option.value === item.size)?.price || 0;
        return sum + price * Number(item.count || 0);
      }, 0);

    const canvasTotal = (values.canvas || []).reduce(
      (sum, item) => sum + Number(item.price || 0),
      0
    );

    const subtotal =
      packagePrice +
      calcItems(values.paper, [
        { value: "A6", price: 2500 },
        { value: "A5", price: 5000 },
        { value: "A4", price: 10000 },
        { value: "A3", price: 15000 },
      ]) +
      calcItems(values.frame, [
        { value: "A5", price: 10000 },
        { value: "A4", price: 15000 },
        { value: "A3", price: 30000 },
      ]) +
      calcItems(values.frameAndPaper, [
        { value: "A5", price: 13000 },
        { value: "A4", price: 20000 },
        { value: "A3", price: 40000 },
      ]) +
      canvasTotal +
      Number(values.addPayment || 0);

    return Math.max(
      0,
      subtotal - Number(values.prePay || 0) - Number(values.minusPayment || 0)
    );
  };

  const isSameEditableBook = useCallback((
    first: Partial<DataType>,
    second: Partial<DataType>
  ) => {
    return (
      JSON.stringify(normalizeEditableBookValues(first)) ===
      JSON.stringify(normalizeEditableBookValues(second))
    );
  }, [normalizeEditableBookValues]);

  useEffect(() => {
    setPage(1);
  }, [
    canvasOnly,
    descriptionSearch,
    selectedDay,
    selectedPackage,
    selectedPaymentType,
    selectedUserRole,
    selectedYear,
  ]);

  const handleEdit = useCallback(
    (record: DataType) => {
      setCurrentRow(record);
      setOriginalRecord(record);
      setModalType("edit");
      setModalOpen(true);
      setIsSubmitDisabled(true);
      const bookingDate = dayjs(`${record.year}-${record.day}`);
      const bookingTime = dayjs(`2000-01-01 ${record.bookedTime}`);

      form.setFieldsValue({
        year: record.year,
        day: record.day,
        bookedTime: record.bookedTime,
        bookingDate: bookingDate.isValid() ? bookingDate : undefined,
        bookingTime: bookingTime.isValid() ? bookingTime : undefined,
        paymenType: record.paymenType,
        paymentBreakdown: normalizePaymentBreakdown(
          record.paymentBreakdown,
          record.paymenType,
          record.postPay
        ),
        packageName: record.packageName,
        prePay: record.prePay,
        postPay: record.postPay,
        addPayment: record.addPayment,
        minusPayment: record.minusPayment,
        giftPhoto: record.giftPhoto,
        pictures: record.pictures,
        paper: record.paper,
        frame: record.frame,
        frameAndPaper: record.frameAndPaper,
        canvas: record.canvas,
        description: record.description,
        pickedUpCanvas: record.pickedUpCanvas,
      });
    },
    [form]
  );

  const handleValuesChange = () => {
    if (originalRecord) {
      const currentValues = form.getFieldsValue();
      form.setFieldsValue({
        postPay: calculateEditablePostPay(currentValues),
      });
      setIsSubmitDisabled(isSameEditableBook(currentValues, originalRecord));
    }
  };

  useEffect(() => {
    const formValues = form.getFieldsValue();
    if (originalRecord) {
      setIsSubmitDisabled(isSameEditableBook(formValues, originalRecord));
    }
  }, [form, isSameEditableBook, originalRecord]);

  useEffect(() => {
    if (currentRow) {
      form.setFieldsValue(currentRow);
    } else {
      form.resetFields();
    }
  }, [currentRow, form]);

  setTimeout(() => {
    if (isFooterVisible) return setIsFooterVisible(false);
  }, 50000);

  const handlePasswordSubmit = () => {
    if (footerPassword === passWord) {
      setIsFooterVisible(!isFooterVisible);
      setModalOpen(false);
      notification.success({
        message: "Зөв",
        description: "Хөл дүн одоо харагдана.",
      });
      setFooterPassword("");
    } else {
      notification.error({
        message: "Буруу",
        description: "Хөл дүн харагдахгүйн дээ.",
      });
    }
    setFooterPassword("");
  };

  const handleClick = async (values: DataType) => {
    if (isSubmitting) return; // prevent double submission
    setIsSubmitting(true);
    const dataToSend = {
      pickedUpCanvas: values.pickedUpCanvas,
    };
    try {
      const response = await axios.put(
        `${API_BASE_URL}/book/updatecanvas/${values._id}`,
        dataToSend
      );
      if (response.status === 200) {
        if (!values.pickedUpCanvas === true) {
          notification.success({
            message: "Амжилттай",
            description: `Энэ дугаартай хүнийг ${values.description} зурагаа аваагүй гэж хадгалагдлаа`,
          });
        } else {
          notification.success({
            message: "Амжилттай",
            description: `Энэ дугаартай хүнийг ${values.description} зурагаа авсан гэж хадгалагдлаа`,
          });
        }
        await refetch();
      }
    } catch (error) {
      notification.error({
        message: "Алдаа",
        description: "Хадгалах үед алдаа гарлаа",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (values: DataType) => {
    if (!currentRow) return;
    if (isSubmitting) return;
    setIsSubmitting(true);

    const dataToSend = normalizeEditableBookValues(values);
    const splitPostPay = getPaymentBreakdownTotal(dataToSend.paymentBreakdown);

    if (splitPostPay !== dataToSend.postPay) {
      notification.error({
        message: "Төлбөрийн дүн таарахгүй байна",
        description: `Бэлэн/Карт/Данс нийлбэр ${splitPostPay.toLocaleString()}₮ байна. Үлдэгдэл ${dataToSend.postPay.toLocaleString()}₮-тэй тэнцэх ёстой.`,
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await axios.put(
        `${API_BASE_URL}/book/update/${currentRow._id}`,
        dataToSend
      );
      if (response.status === 200) {
        notification.success({
          message: "Амжилттай",
          description: "Засалт хадгалагдлаа",
        });
        setModalOpen(false);
        form.resetFields();
        setOriginalRecord(null);
        setIsSubmitDisabled(true);
        await refetch();
      }
    } catch (error) {
      notification.error({
        message: "Алдаа",
        description: "Хадгалах үед алдаа гарлаа",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const passWord = process.env.REACT_APP_FOOTER_AMOUNT_PASSWORD;

  const handleDelete = async () => {
    if (isSubmitting) return; // prevent double submission
    setIsSubmitting(true);
    const formValues = form.getFieldsValue();
    if (!formValues.password) {
      notification.error({
        message: "Анхаар",
        description: "Нууц үгээ оруулна уу!",
      });
      return;
    }
    if (passWord !== formValues.password) {
      notification.error({
        message: "Анхаар",
        description: "Нууц үг буруу байна!",
      });
      return;
    }

    if (passWord === formValues.password) {
      try {
        const response = await axios.delete(
          `${API_BASE_URL}/book/delete/${currentRow?._id}`
        );
        if (response.status === 200) {
          notification.success({
            message: "Зөв",
            description: "Амжилттай устгагдлаа",
          });

          setModalOpen(false);
          refetch();
          form.resetFields();
        }
      } catch (error) {
        console.error("Error deleting record:", error);
        notification.success({
          message: "Буруу",
          description: "Устгахад алдаа гарлаа",
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const formatToK = (value: number): string => {
    if (value >= 1000) {
      return `${value / 1000}k`;
    }
    return `${value}`;
  };
  const userRole = localStorage.getItem("userRole");

  const columns: ColumnType<DataType>[] = [
    {
      title: <EditOutlined />,
      render: (text: string, record: DataType) => (
        <EditOutlined
          type="primary"
          style={{
            fontSize: "10px",
            fontWeight: "bold",
            width: "15px",
            height: "15px",
          }}
          onClick={() => handleEdit(record)}
        >
          Засах
        </EditOutlined>
      ),
    },
    ...(userRole === "admin"
      ? [
          {
            title: <DeleteOutlined />,
            render: (text: string, record: DataType) => (
              <DeleteOutlined
                type="primary"
                style={{
                  fontSize: "10px",
                  fontWeight: "bold",
                  width: "15px",
                  height: "15px",
                  color: "red",
                }}
                onClick={() => openModal("delete", record)}
              />
            ),
          },
        ]
      : []),
    {
      title: (
        <Tooltip title="User">
          <div
            style={{
              maxWidth: 80,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <UserOutlined />
          </div>
        </Tooltip>
      ),
      dataIndex: "createdBy",
      key: "createdBy",
      render: (createdBy: string | BookUser) => getCreatedByName(createdBy),
    },
    {
      title: (
        <Tooltip title="User">
          <div
            style={{
              maxWidth: 80,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Day
          </div>
        </Tooltip>
      ),
      dataIndex: "day",
      key: "day",
      sorter: (a: DataType, b: DataType) => a.day.localeCompare(b.day),
    },
    {
      title: "Цаг",
      dataIndex: "bookedTime",
      key: "bookedTime",
      sorter: (a: DataType, b: DataType) =>
        a.bookedTime.localeCompare(b.bookedTime),
    },
    {
      title: "Багц",
      dataIndex: "packageName",
      key: "packageName",
      sorter: (a, b) => a.packageName.localeCompare(b.packageName),
      render: (_text, record) => (
        <>
          <Tooltip
            title={
              record.packageName + " Багцын дэлгэрэнгүй мэдээлэл дарж харна уу"
            }
          >
            <span
              onClick={() => {
                showDrawer(record);
              }}
              style={{ color: "var(--color-primary)", cursor: "pointer" }}
            >
              {record.packageName}
              {record.giftCardCode ? ` (Gift card: ${record.giftCardCode})` : ""}
            </span>
          </Tooltip>
        </>
      ),
    },
    {
      title: (
        <Tooltip title="Урьдчилсан төлбөр">
          <div
            style={{
              maxWidth: 80,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Урьдчилсан төлбөр
          </div>
        </Tooltip>
      ),
      dataIndex: "prePay",
      key: "prePay",
      render: (value: number) => formatToK(value),
    },
    {
      title: (
        <Tooltip title="Үлдэгдэл төлбөр">
          <div
            style={{
              maxWidth: 80,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Үлдэгдэл төлбөр
          </div>
        </Tooltip>
      ),
      dataIndex: "postPay",
      key: "postPay",
      render: (value: number) => formatToK(value),
    },
    {
      title: (
        <Tooltip title="+лт төлбөр">
          <div
            style={{
              maxWidth: 80,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            +лт төлбөр
          </div>
        </Tooltip>
      ),
      dataIndex: "addPayment",
      key: "addPayment",
      render: (value: number | undefined) => formatToK(value ?? 0),
    },
    {
      title: (
        <Tooltip title="Discount">
          <div
            style={{
              maxWidth: 80,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Discount
          </div>
        </Tooltip>
      ),
      dataIndex: "minusPayment",
      key: "minusPayment",
      render: (value: number | undefined) => formatToK(value ?? 0),
    },

    {
      title: (
        <Tooltip title="Дагалдах">
          <div
            style={{
              maxWidth: 80,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Дагалдах
          </div>
        </Tooltip>
      ),
      dataIndex: "pictures",
      key: "pictures",

      render: (pictures: GiftPhoto[]) => (
        <div>
          {pictures.map((picture) => (
            <div key={picture._id}>
              {picture.size}, {picture.count}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: (
        <Tooltip title="+лт зураг">
          <div
            style={{
              maxWidth: 80,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            +лт зураг
          </div>
        </Tooltip>
      ),
      dataIndex: "paper",
      key: "paper",

      render: (pictures: Paper[]) => (
        <div>
          {pictures.map((picture) => (
            <div key={picture._id}>
              {picture.size}, {picture.count}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: (
        <Tooltip title="Жааз">
          <div
            style={{
              maxWidth: 80,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Жааз
          </div>
        </Tooltip>
      ),
      dataIndex: "frame",
      key: "frame",

      render: (pictures: Frame[]) => (
        <div>
          {pictures.map((picture) => (
            <div key={picture._id}>
              {picture.size}, {picture.count}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: (
        <Tooltip title="Жааз + зураг">
          <div
            style={{
              maxWidth: 80,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Жааз + зураг
          </div>
        </Tooltip>
      ),

      dataIndex: "frameAndPaper",
      key: "frameAndPaper",

      render: (pictures: FrameAndPaper[]) => (
        <div>
          {pictures.map((picture) => (
            <div key={picture._id}>
              {picture.size}, {picture.count}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: (
        <Tooltip title="Canvas">
          <div
            style={{
              maxWidth: 80,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Canvas
          </div>
        </Tooltip>
      ),
      width: 180,
      // ellipsis: true,
      dataIndex: "canvas",
      key: "canvas",
      render: (pictures: Canvas[] | undefined) => (
        <div>
          {(pictures ?? []).map((picture: Canvas) => (
            <Tooltip
              key={picture._id}
              title={`Size: ${picture.size}, Code: ${picture.code}, Price: ${picture.price}`}
            >
              <span
                style={{
                  maxWidth: 70,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "inline-block",
                  cursor: "default", // ← This prevents the hover pointer
                }}
              >
                {picture.size || ""}, code: {picture.code || ""}
              </span>
            </Tooltip>
          ))}
        </div>
      ),
    },
    {
      title: (
        <Tooltip title="Canvas ?">
          <div
            style={{
              maxWidth: 80,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Canvas ?
          </div>
        </Tooltip>
      ),
      key: "pickedUpCanvas",
      render: (_, record: DataType) => {
        const hasCanvas =
          Array.isArray(record.canvas) && record.canvas.length > 0;
        return hasCanvas ? (
          <Checkbox
            disabled={isSubmitting || record.pickedUpCanvas} // Disable if submitting or already picked up
            style={{
              cursor:
                isSubmitting || record.pickedUpCanvas
                  ? "not-allowed"
                  : "pointer",
            }}
            checked={record.pickedUpCanvas}
            onChange={(e) =>
              handleClick({ ...record, pickedUpCanvas: e.target.checked })
            }
          />
        ) : null;
      },
    },
    {
      title: (
        <Tooltip title="Type">
          <div
            style={{
              maxWidth: 80,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Type
          </div>
        </Tooltip>
      ),
      dataIndex: "paymenType",
      key: "paymenType",
      render: (text: string, record: DataType) => {
        const paymentBreakdown = normalizePaymentBreakdown(
          record.paymentBreakdown,
          text,
          record.postPay
        );
        const paymentSummary = [
          paymentBreakdown.cash > 0 ? `Бэлэн ${formatToK(paymentBreakdown.cash)}` : "",
          paymentBreakdown.card > 0 ? `Карт ${formatToK(paymentBreakdown.card)}` : "",
          paymentBreakdown.account > 0
            ? `Данс ${formatToK(paymentBreakdown.account)}`
            : "",
        ]
          .filter(Boolean)
          .join(" | ");

        if (paymentSummary) {
          return <Tooltip title={paymentSummary}>{text === "Mixed" ? "Mixed" : paymentSummary}</Tooltip>;
        }

        switch (text?.toLowerCase()) {
          case "cash":
            return (
              <Tooltip title="Бэлэнээр">
                <DollarOutlined style={{ color: "#52c41a" }} />
              </Tooltip>
            );
          case "card":
            return (
              <Tooltip title="Картаар">
              <CreditCardOutlined style={{ color: "var(--color-primary)" }} />
              </Tooltip>
            );
          case "account":
            return (
              <Tooltip title="Дансаар">
                <BankOutlined style={{ color: "#faad14" }} />
              </Tooltip>
            );
          default:
            return text;
        }
      },
    },
    {
      title: (
        <Tooltip title="Тайлбар">
          <div
            style={{
              maxWidth: 70,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Тайлбар
          </div>
        </Tooltip>
      ),
      dataIndex: "description",
      key: "description",
      render: (text: string) => (
        <Tooltip title={text}>
          <span
            style={{
              maxWidth: 70,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "inline-block",
              cursor: "default",
            }}
          >
            {text}
          </span>
        </Tooltip>
      ),
    },
  ];
  const filteredData = bookingData?.data || [];
  const totalRecords = bookingData?.total || 0;
  const filterRows = filterSourceData.length > 0 ? filterSourceData : filteredData;
  const availableYears = Array.from(
    new Set(
      [currentYear, ...filterRows.map((item) => item.year).filter(Boolean)].map(
        String
      )
    )
  ).sort((a, b) => b.localeCompare(a));

  const handlePaginationChange = (nextPage: number, nextPageSize: number) => {
    setPage(nextPageSize !== pageSize ? 1 : nextPage);
    setPageSize(nextPageSize);
  };

  const availableDays = Array.from(
    new Set(filterRows.map((item) => item.day).filter(Boolean))
  );
  const sortedAvailableDays = sortDaysFromToday(availableDays, selectedYear);
  const availablePaymentTypes = Array.from(
    new Set(
      filterRows
        .flatMap((item) => {
          const paymentBreakdown = normalizePaymentBreakdown(
            item.paymentBreakdown,
            item.paymenType,
            item.postPay
          );
          const values = [
            paymentBreakdown.cash > 0 ? "Cash" : "",
            paymentBreakdown.card > 0 ? "Card" : "",
            paymentBreakdown.account > 0 ? "Account" : "",
          ].filter(Boolean);

          return values.length > 0 ? values : [item.paymenType];
        })
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));
  const availablePackages = Array.from(
    new Set(filterRows.map((item) => item.packageName).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));
  const createdByOptions = useMemo<UserFilterOption[]>(() => {
    const optionMap = new Map<string, UserFilterOption>();
    const labels = new Set<string>();

    const addOption = (value?: string, label?: string) => {
      const safeValue = String(value || "").trim();
      const safeLabel = String(label || "").trim();
      const labelKey = safeLabel.toLowerCase();

      if (!safeValue || !safeLabel || labels.has(labelKey)) return;

      optionMap.set(safeValue, {
        label: safeLabel,
        value: safeValue,
        searchText: `${safeLabel} ${safeValue}`.toLowerCase(),
      });
      labels.add(labelKey);
    };

    authUsers.forEach((user) => {
      addOption(
        user._id,
        user.fullName || user.name || user.userrealname || user.username
      );
    });

    filterRows.forEach((item) => {
      const createdBy = item.createdBy;

      if (!createdBy) return;

      if (typeof createdBy === "string") {
        addOption(createdBy, createdBy);
        return;
      }

      addOption(
        createdBy._id || createdBy.username || createdBy.userrealname,
        getCreatedByName(createdBy)
      );
    });

    return Array.from(optionMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [authUsers, filterRows]);

  if (loading)
    return (
      <div
        style={{
          textAlign: "center",
          display: "flex",
          minHeight: "100vh",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
      </div>
    );
  if (error)
    return (
      <Alert
        message="Error fetching data"
        description={error.message}
        type="error"
      />
    );

  const totalPrePay = filteredData.reduce(
    (acc, item) => acc + (item.prePay || 0),
    0
  );

  const totalPostPay = filteredData.reduce(
    (acc, item) => acc + (item.postPay || 0),
    0
  );
  const openModal = (type: typeof modalType, row?: any) => {
    if (type === "edit" && row) {
      form.setFieldsValue(row);
    }
    setCurrentRow(row || null);
    setModalType(type);
    setModalOpen(true);
  };

  const getPackageColor = (record: BookingSiteBooking): string => {
    const pkg = flatPackages.find((p) =>
      getPackageMatchScore(record.eventTitle, [p.label, p.value]) >=
      PACKAGE_MATCH_THRESHOLD
    );

    if (!pkg) return "yellow";

    // BONUS LOGIC (price-based)
    if (pkg.price >= 100000) return "red";
    if (pkg.price >= 80000) return "orange";
    if (pkg.price >= 60000) return "green";
    if (pkg.price >= 40000) return "green";

    return "default";
  };

  const renderBookingSiteDetails = (booking: BookingSiteBooking) => {
    const meta = parseBookingSiteNote(booking.note);

    return (
      <Descriptions bordered column={1} size="small">
        <Descriptions.Item label="Багц" style={{ color: getPackageColor(booking) }}>
          {booking.eventTitle}
        </Descriptions.Item>
        <Descriptions.Item label="Нэр">{booking.name || "-"}</Descriptions.Item>
        <Descriptions.Item label="Утас">{booking.phone || "-"}</Descriptions.Item>
        <Descriptions.Item label="Имэйл">{booking.email || "-"}</Descriptions.Item>
        <Descriptions.Item label="Хүний тоо">{meta.quantity || "-"}</Descriptions.Item>
        {meta.childGender && (
          <Descriptions.Item label="Хүүхдийн хүйс">
            {meta.childGender}
          </Descriptions.Item>
        )}
        {meta.gift && (
          <Descriptions.Item label="Gift card">{meta.gift}</Descriptions.Item>
        )}
        <Descriptions.Item label="Цаг">
          {new Date(`${booking.date}T${booking.time}:00+08:00`).toLocaleString("en-US", {
            timeZone: ULAANBAATAR_TIME_ZONE,
          })}
        </Descriptions.Item>
        <Descriptions.Item label="Захиалга үүссэн огноо">
          {booking.createdAt ? new Date(booking.createdAt).toLocaleString() : "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Status">{booking.status || "active"}</Descriptions.Item>
      </Descriptions>
    );
  };

  return (
    <div
      className={`book-list-page custom-table ${
        currentTheme === "dark" ? "dark-theme" : "light-theme"
      }`}
    >
      <Form form={form} layout="vertical" className="book-filter-panel">
        <Row gutter={[16, 8]}>
          <Col xs={24} sm={12} md={6} lg={3}>
            <Form.Item
              label={<span style={{ whiteSpace: "nowrap" }}>Он</span>}
              name="filterYear"
              initialValue={currentYear}
            >
              <Select
                onChange={(value) => {
                  setSelectedYear(value || currentYear);
                  setSelectedDay(null);
                  form.setFieldValue("filterDay", undefined);
                }}
                options={availableYears.map((year) => ({
                  label: year,
                  value: year,
                }))}
                placeholder="Он"
                showSearch
                optionFilterProp="label"
                listHeight={256}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Form.Item
              label={<span style={{ whiteSpace: "nowrap" }}>Өдөр</span>}
              name="filterDay"
            >
              <Select
                onChange={(value) => setSelectedDay(value || null)}
                options={sortedAvailableDays.map((day) => ({
                  label: day,
                  value: day,
                }))}
                placeholder="Өдөр сонгоно уу"
                allowClear
                showSearch
                optionFilterProp="label"
                listHeight={256}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <Form.Item
              label={
                <span style={{ whiteSpace: "nowrap" }}>Төлбөрийн хэлбэр</span>
              }
              name="filterPaymenType"
            >
              <Select
                onChange={(value) => setSelectedPaymentType(value || null)}
                options={availablePaymentTypes.map((type) => ({
                  label: type,
                  value: type,
                }))}
                placeholder="Төлбөрийн хэлбэр сонгоно уу"
                allowClear
                showSearch
                optionFilterProp="label"
                listHeight={256}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <Form.Item
              label={
                <span style={{ whiteSpace: "nowrap" }}>
                  Бүртгэсэн хэрэглэгч
                </span>
              }
              name="filterCreatedBy"
            >
              <Select
                onChange={(value) => setSelectedUserRole(value || "")}
                options={createdByOptions}
                placeholder="Бүртгэсэн хэрэглэгч сонгоно уу"
                allowClear
                showSearch
                optionFilterProp="searchText"
                listHeight={256}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <Form.Item
              labelCol={{ span: 24 }}
              label={<span style={{ whiteSpace: "nowrap" }}>Багц</span>}
              name="PackageName"
            >
              <Select
                mode="multiple"
                onChange={(values) =>
                  setSelectedPackage(values?.length ? values : null)
                }
                options={availablePackages.map((pkg) => ({
                  label: pkg,
                  value: pkg,
                }))}
                placeholder="Багц сонгоно уу"
                allowClear
                showSearch
                optionFilterProp="label"
                listHeight={256}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Form.Item
              label={<span style={{ whiteSpace: "nowrap" }}>Тайлбар хайх</span>}
              name="filterDescriptionSearch"
            >
              <Input
                allowClear
                placeholder="Утас, тэмдэглэл гэх мэт"
                onChange={(e) => setDescriptionSearch(e.target.value)}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Form.Item
            label={
              <span style={{ whiteSpace: "nowrap" }}>Canvas-авсан эсэх?</span>
            }
          >
            <Col
              xs={24}
              sm={12}
              md={6}
              lg={4}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Checkbox onChange={(e) => setCanvasOnly(e.target.checked)} />
            </Col>
          </Form.Item>

          {userRole === "admin" && (
            <Form.Item
              label={
                <span style={{ marginLeft: 16, whiteSpace: "nowrap" }}>
                  Хөл дүн
                </span>
              }
            >
              <Col
                xs={24}
                sm={12}
                md={6}
                lg={3}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    marginLeft: 16,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <EyeOutlined
                    style={{
                      fontSize: 18,
                      cursor: "pointer",
                      marginLeft: 8,
                      marginBottom: "10px",
                    }}
                    onClick={() => openModal("password", null)}
                  />
                </div>
              </Col>
            </Form.Item>
          )}
        </Row>
      </Form>
      {loading ? (
        <Spin size="large" />
      ) : error ? (
        <Alert message="Error fetching data" description={error} type="error" />
      ) : isMobile ? (
        <div className="book-card-grid">
          {filteredData.map((record) => (
            <div key={record._id} className="card-item">
              <p>
                <strong>Өдөр:</strong> {record.day}
              </p>
              <p>
                <strong>Цаг:</strong> {record.bookedTime}
              </p>
              <p
                onClick={() => {
                  showDrawer(record);
                }}
                style={{ color: "var(--color-primary)", cursor: "pointer" }}
              >
                <strong>Багц:</strong> {record.packageName}
              </p>
              <p>
                <strong>Урьдчилгаа:</strong> {record.prePay}
              </p>
              <p>
                <strong>Үлдэгдэл:</strong> {record.postPay}
              </p>
              <p>
                <strong>Canvas </strong>
                {record.canvas.map((e) => (
                  <div key={e._id}>
                    Code: {e.code}, Price: {e.price}, Size: {e.size}
                  </div>
                ))}
              </p>
              <p>
                <strong>giftPhoto </strong>
                {record.pictures.map((e) => (
                  <div key={e._id}>
                    Size: {e.size}, Count: {e.count}
                  </div>
                ))}
              </p>
              <p>
                <strong>Paper </strong>
                {record.paper.map((e) => (
                  <div key={e._id}>
                    Size: {e.size}, Count: {e.count}
                  </div>
                ))}
              </p>
              <p>
                <strong>Frame </strong>
                {record.frame.map((e) => (
                  <div key={e._id}>
                    Size: {e.size}, Count: {e.count}
                  </div>
                ))}
              </p>
              <p>
                <strong>Frame and Paper </strong>
                {record.frameAndPaper.map((e) => (
                  <div key={e._id}>
                    Size: {e.size}, Count: {e.count}
                  </div>
                ))}
              </p>

              <p>
                <strong>Төлбөрийн хэлбэр:</strong> {record.paymenType}
              </p>
              <p>
                <strong>Бүртгэсэн хэрэглэгч:</strong>{" "}
                {getCreatedByName(record.createdBy)}
              </p>
              <p>
                <strong>Canvas авсан уу? </strong>
                {record.pickedUpCanvas ? "Тийм" : "Үгүй"}
              </p>

              <p>
                <strong>+т төлбөр:</strong> {record.addPayment}
              </p>
              <p>
                <strong>DisCount:</strong> {record.minusPayment}
              </p>

              <p>
                <strong>Тайлбар:</strong> {record.description}
              </p>
              <div className="book-card-actions">
                <Button
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => openModal("view", record)}
                />
                <Button
                  size="small"
                  type="primary"
                  onClick={() => handleEdit(record)}
                >
                  Засах
                </Button>
                {userRole === "admin" && (
                  <>
                    <DeleteOutlined
                      style={{ color: "red", cursor: "pointer" }}
                      onClick={() => openModal("delete", record)}
                    />
                  </>
                )}
              </div>
            </div>
          ))}
          <Pagination
            current={page}
            pageSize={pageSize}
            total={totalRecords}
            showSizeChanger
            pageSizeOptions={["20", "50", "100"]}
            onChange={handlePaginationChange}
            style={{
              marginTop: 16,
              display: "flex",
              justifyContent: "center",
            }}
          />
        </div>
      ) : (
        <div className="book-table-panel">
          <Table
            dataSource={filteredData}
            columns={columns}
            rowKey="_id"
            style={{ maxWidth: "100vw" }}
            // scroll={{ x: "120vw", y: "calc(100vh - 250px)" }}
            bordered
            pagination={{
              current: page,
              pageSize,
              total: totalRecords,
              showSizeChanger: true,
              pageSizeOptions: ["20", "50", "100"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} / ${total}`,
              onChange: handlePaginationChange,
              position: ["bottomCenter"],
            }}
            className="custom-table"
            footer={() =>
              isFooterVisible ? (
                <div className="table-footer">
                  <Typography.Text>
                    Урьдчилгаа: {formatToK(totalPrePay)} | Үлдэгдэл:{" "}
                    {formatToK(totalPostPay)} | Нийт :{" "}
                    {formatToK(totalPrePay + totalPostPay)}
                  </Typography.Text>
                </div>
              ) : (
                <div
                  className={`table-footer ${
                    currentTheme === "dark" ? "dark-footer" : "light-footer"
                  }`}
                >
                  <Typography.Text>Хөл дүн алга</Typography.Text>
                </div>
              )
            }
          />
        </div>
      )}
      <CustomModal
        modalType={modalType}
        modalOpen={modalOpen}
        isSubmitting={isSubmitting}
        setModalOpen={setModalOpen}
        currentRow={currentRow}
        form={form}
        footerPassword={footerPassword}
        setFooterPassword={setFooterPassword}
        handlePasswordSubmit={handlePasswordSubmit}
        handleDelete={handleDelete} // implement as needed
        handleUpdate={() => {
          if (currentRow) {
            form
              .validateFields()
              .then((values) => handleUpdate(values as DataType));
          }
        }}
        handleValuesChange={handleValuesChange}
        isSubmitDisabled={isSubmitDisabled}
        packageGroups={packages}
      />
      <Drawer
        size="large"
        title="Багцийн мэдээлэл"
        onClose={onClose}
        open={open}
        style={{ color: "var(--text)" }}
        className={currentTheme === "dark" ? "dark-theme" : "light-theme"}
        // bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        {bookingSiteMatches.length > 0 ? (
          <>
            <Title
              level={4}
              style={{ color: "var(--text-strong)" }}
            >
              booking.picshot.online мэдээлэл
              {bookingSiteMatches.length > 1 ? ` (${bookingSiteMatches.length})` : ""}
            </Title>
            <Carousel
              arrows={bookingSiteMatches.length > 1}
              dots={bookingSiteMatches.length > 1}
              infinite={false}
            >
              {bookingSiteMatches.map((booking) => (
                <div key={booking.id}>{renderBookingSiteDetails(booking)}</div>
              ))}
            </Carousel>
          </>
        ) : currentRow ? (
          <Alert
            message="booking.picshot.online мэдээлэл олдсонгүй"
            description="Ижил өдөр, ойролцоо цаг эсвэл багцын нэрээр таарсан active booking.picshot.online захиалга олдсонгүй."
            type="warning"
            showIcon
          />
        ) : null}

        {currentRow && (
          <>
            <Divider
              style={{ color: "var(--text)" }}
            />
            <Title
              level={4}
              style={{ color: "var(--text-strong)" }}
            >
              Бүртгэлийн мэдээлэл
            </Title>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Өдөр">
                {currentRow.day}
              </Descriptions.Item>
              <Descriptions.Item label="Цаг">
                {currentRow.bookedTime}
              </Descriptions.Item>
              <Descriptions.Item label="Багц">
                {currentRow.packageName}
              </Descriptions.Item>
              {currentRow.giftCardCode && (
                <Descriptions.Item label="Gift card">
                  {currentRow.giftCardCode}
                </Descriptions.Item>
              )}
              {currentRow.giftCardContact && (
                <Descriptions.Item label="Gift card холбоо барих">
                  {currentRow.giftCardContact}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Урьдчилгаа">
                {currentRow.prePay}
              </Descriptions.Item>
              <Descriptions.Item label="Үлдэгдэл">
                {currentRow.postPay}
              </Descriptions.Item>
              <Descriptions.Item label="Төлбөрийн хэлбэр">
                {currentRow.paymenType}
              </Descriptions.Item>
              <Descriptions.Item label="Бүртгэсэн хэрэглэгч">
                {getCreatedByName(currentRow.createdBy)}
              </Descriptions.Item>
              <Descriptions.Item label="Canvas авсан уу?">
                {currentRow.pickedUpCanvas ? "Тийм" : "Үгүй"}
              </Descriptions.Item>
              <Descriptions.Item label="+лт төлбөр">
                {currentRow.addPayment || 0}
              </Descriptions.Item>
              <Descriptions.Item label="DisCount">
                {currentRow.minusPayment || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Тайлбар">
                {currentRow.description || "-"}
              </Descriptions.Item>
            </Descriptions>

            {/* Nested details */}
            {currentRow.canvas?.length > 0 && (
              <>
                <Divider orientation="left">Canvas</Divider>
                {currentRow.canvas.map((e: Canvas) => (
                  <p key={e._id}>
                    Code: {e.code}, Price: {e.price}, Size: {e.size}
                  </p>
                ))}
              </>
            )}

            {currentRow.pictures?.length > 0 && (
              <>
                <Divider orientation="left">Дагалдах Зураг</Divider>
                {currentRow.pictures.map((e: GiftPhoto) => (
                  <p key={e._id}>
                    Size: {e.size}, Count: {e.count}
                  </p>
                ))}
              </>
            )}

            {currentRow.paper?.length > 0 && (
              <>
                <Divider orientation="left">+лт зураг</Divider>
                {currentRow.paper.map((e: Paper) => (
                  <p key={e._id}>
                    Size: {e.size}, Count: {e.count}
                  </p>
                ))}
              </>
            )}

            {currentRow.frame?.length > 0 && (
              <>
                <Divider orientation="left">Жааз</Divider>
                {currentRow.frame.map((e: Frame) => (
                  <p key={e._id}>
                    Size: {e.size}, Count: {e.count}
                  </p>
                ))}
              </>
            )}

            {currentRow.frameAndPaper?.length > 0 && (
              <>
                <Divider orientation="left">Жаазтай зураг</Divider>
                {currentRow.frameAndPaper.map((e: FrameAndPaper) => (
                  <p key={e._id}>
                    Size: {e.size}, Count: {e.count}
                  </p>
                ))}
              </>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default MyComponent;
